import api from "api"

const retryUntil = (func, interval, retries, ...args) => {
  return new Promise(function(resolve) {
    const timer = setInterval(async () => {
      await func(clearTimer, ...args)
    }, interval)
    const clearTimer = () => clearInterval(timer)
    if (retries > 0) {
      setTimeout(function() {
        clearInterval(timer)
        resolve()
      }, interval * retries)
    }
  })
}

export async function loadHouseholdAccounts(store, household_id) {
  const accounts = await api.get(`households/${household_id}/accounts`)
  const household = await store.actions.loadHousehold(household_id)
  await store.update(state => {
    state.householdAccountIds[household_id] = accounts.map(
      account => account.id
    )

    accounts.forEach(account => {
      state.accounts[account.id] = account
    })
  })
  if (
    !household.has_confirmed_accounts &&
    accounts.length > 0 &&
    accounts.filter(({ is_confirmed }) => is_confirmed).length === 0
  ) {
    await store.actions.updateHousehold({
      id: household_id,
      has_confirmed_accounts: true
    })
  }
}

export async function loadHouseholdAccount(store, { id, household_id }) {
  const account = await api.get(`accounts/${id}`)
  await store.update(state => {
    state.householdAccountIds[household_id].push(account.id)
    state.accounts[account.id] = account
  })
}

export async function loadAccount(store, account_id) {
  const account = await api.get(`accounts/${account_id}`)
  await store.update(state => {
    state.accounts[account.id] = account
  })
}

export async function addAccount(store, { household_id, ...accountDetails }) {
  if (accountDetails.person_id === "null") {
    accountDetails.person_id = null
  }

  const account = await api.post(
    `households/${household_id}/accounts`,
    undefined,
    accountDetails
  )

  await store.update(state => {
    if (!state.householdAccountIds[household_id]) {
      state.householdAccountIds[household_id] = []
    }
    state.householdAccountIds[household_id].push(account.id)
    state.accounts[account.id] = account
    delete state.incomeBreakdowns[household_id]
  })

  return account
}

export async function updateAccount(store, { id, ...accountDetails }) {
  if (accountDetails.person_id === "null") {
    accountDetails.person_id = null
  }

  const account = await api.patch(`accounts/${id}`, undefined, accountDetails)

  await store.update(state => {
    state.accounts[account.id] = account
    delete state.incomeBreakdowns[account.household_id]
  })
}

export async function deleteAccount(store, { household_id, id }) {
  await api.delete(`accounts/${id}`)
  await store.update(state => {
    state.householdAccountIds[household_id].splice(
      state.householdAccountIds[household_id].findIndex(
        accountId => accountId === id
      ),
      1
    )
    delete state.accounts[id]
    delete state.incomeBreakdowns[household_id]
  })
}

export async function loadHouseholdConnections(store, household_id) {
  const connections = await api.get(`households/${household_id}/connections`)
  await store.update(state => {
    connections.map(account => (state.connections[account.id] = account))
    const keys = connections.map(acc => acc.id)
    state.householdConnectionsIds[household_id] = keys
  })
}

export async function loadHouseholdConnectionLinks(store, household_id) {
  const connectionLinks = await api.get(
    `households/${household_id}/connections/links`
  )
  await store.update(state => {
    const keys = Object.keys(connectionLinks)
    state.householdConnectionLinksIds[household_id] = keys
    keys.map(key => (state.connectionLinks[key] = connectionLinks[key]))
  })
}

export async function monitorConnectionRefresh(store, household_id) {
  await store.actions.updateHouseholdConnectionsRefreshStatus(household_id)

  const checkRefreshStatus = async clearRetry => {
    let householdState = store.getState("householdState", household_id)
    if (!householdState || !householdState["isRefreshingAccounts"]) {
      return
    }

    await store.actions.updateHouseholdConnectionsRefreshStatus(household_id)
    householdState = store.getState("householdState", household_id)
    if (!householdState || !householdState["isRefreshingAccounts"]) {
      await store.actions.loadHouseholdAccounts(household_id)
      clearRetry()
    }
  }

  retryUntil(checkRefreshStatus, 1000, 10)
    .then(() => retryUntil(checkRefreshStatus, 5000, 10))
    .then(() => retryUntil(checkRefreshStatus, 10000, 10))
    .then(() => retryUntil(checkRefreshStatus, 20000, 10))
    .then(() => retryUntil(checkRefreshStatus, 30000, 10))
    .then(() => retryUntil(checkRefreshStatus, 60000, -1))

  await store.actions.loadHouseholdAccounts(household_id)
}

export async function refreshHouseholdConnections(store, household_id) {
  await api.post(`households/${household_id}/connections/refresh`)
  await store.actions.monitorConnectionRefresh(household_id)
}

export async function updateHouseholdConnectionsRefreshStatus(
  store,
  household_id
) {
  const { isRefreshingAccounts: wasRefreshingAccounts } = store.getState(
    "householdState",
    household_id
  )
  const { status, connection_ids } = await api.get(
    `households/${household_id}/connections/refresh`
  )
  const isRefreshingAccounts = status === "in_progress"
  if (wasRefreshingAccounts && !isRefreshingAccounts) {
    await store.actions.loadHouseholdAccounts(household_id)
    await store.actions.loadHouseholdConnectionLinks(household_id)
    await store.actions.loadHouseholdConnections(household_id)
  }

  await store.actions.updateHouseholdState({
    id: household_id,
    isRefreshingAccounts,
    refreshingConnectingIds: connection_ids
  })
}

export async function ignoreConnectionAccount(
  store,
  { household_id, connection_id }
) {
  const connection = Object.values(store.getState("connections")).find(
    ({ id }) => id === connection_id
  )
  const account = Object.values(store.getState("accounts")).find(
    ({ connection_id: account_connection_id }) =>
      account_connection_id === connection_id
  )
  await store.actions.deleteAccount({ household_id, id: account.id })
  await store.update(state => {
    state.connections[connection.id]["type"] = "ignored"
  })
}

export async function unIgnoreConnectionAccount(store, { household_id, id }) {
  const account = Object.values(store.getState("connections")).find(
    ({ id: accountId }) => id === accountId
  )
  await api.post(`connections/${id}/activate`)
  await store.actions.loadHouseholdAccounts(household_id)
  await store.actions.loadHouseholdConnections(household_id)
  await store.update(state => {
    state.connections[account.id]["type"] = "account"
  })
}

export async function deleteConnectionLink(
  store,
  { household_id, provider_id }
) {
  await api.delete(
    `households/${household_id}/connections/links/${provider_id}`,
    {
      wait: null
    }
  )

  await store.update(state => {
    state.householdConnectionLinksIds[household_id].splice(
      state.householdConnectionLinksIds[household_id].findIndex(
        id => id === provider_id
      ),
      1
    )
    delete state.connectionLinks[provider_id]

    const accounts = store.getState("accounts")
    Object.values(accounts).forEach(acc => {
      if (acc.connection_link_id === provider_id) {
        delete state.accounts[acc.id]
      }
    })
  })

  await store.actions.loadHouseholdAccounts(household_id)
}
